// 📊 ANALYTICS TRACKING SERVICE - Rainbow Sails
// Tracks visitor behavior and sends analytics to backend

import logger from '../utils/logging';
import { generateSessionId } from '../utils/sessionId';
import { API_BASE_URL, BUSINESS_ID } from '../config/api';
import { getAttribution } from '../utils/attribution';
import { getDeviceType } from '../utils/deviceType';

class AnalyticsService {
  constructor() {
    console.log('🔍 [HYDRATION DEBUG] AnalyticsService constructor called');
    console.log('🔍 [HYDRATION DEBUG] Window available in constructor:', typeof window !== 'undefined');
    
    // Use the unified API config (automatically detects production vs development)
    // API_BASE_URL already includes /api/v1, so we need to remove it for the base URL
    this.backendUrl = API_BASE_URL.replace('/api/v1', '');
    
    // Only generate session data on client side
    if (typeof window !== 'undefined') {
      this.sessionId = generateSessionId();
      this.sessionStart = Date.now();
      console.log('🔍 [HYDRATION DEBUG] Generated sessionId:', this.sessionId);
    } else {
      this.sessionId = 'server-side';
      this.sessionStart = 0;
      console.log('🔍 [HYDRATION DEBUG] Using server-side sessionId');
    }
    
    this.pageViews = [];
    this.events = [];
    this.isEnabled = true;
    
    // Initialize session tracking
    this.initializeSession();
    
    console.log('📊 [ANALYTICS] Analytics service initialized', {
      sessionId: this.sessionId,
      timestamp: new Date().toISOString()
    });
  }

  initializeSession() {
    // Track visitor info
    this.visitorInfo = {
      sessionId: this.sessionId,
      userAgent: navigator.userAgent,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      referrer: document.referrer || 'direct',
      landingPage: window.location.pathname,
      timestamp: new Date().toISOString()
    };
    
    // Send session start event
    this.trackEvent('session_start', this.visitorInfo);
  }

  // Page View Tracking
  trackPageView(pageName, path, metadata = {}) {
    const pageView = {
      sessionId: this.sessionId,
      pageName,
      path,
      timestamp: new Date().toISOString(),
      timeOnPreviousPage: this.calculateTimeOnPage(),
      ...metadata
    };
    
    this.pageViews.push(pageView);
    this.currentPageStart = Date.now();
    this.currentPage = pageName;
    
    logger.logAnalytics('page_view', pageView);
    
    // Send to backend
    this.sendToBackend('pageview', pageView);
  }

  calculateTimeOnPage() {
    if (!this.currentPageStart) return 0;
    return Math.floor((Date.now() - this.currentPageStart) / 1000); // seconds
  }

  // Product Tracking
  trackProductView(product) {
    const event = {
      sessionId: this.sessionId,
      eventType: 'product_view',
      productId: product.id,
      productName: product.name,
      productPrice: product.price,
      productCategory: product.category,
      timestamp: new Date().toISOString()
    };
    
    logger.logAnalytics('product_view', event);
    this.trackEvent('product_view', event);
  }

  trackProductClick(product, location = 'gallery') {
    const event = {
      sessionId: this.sessionId,
      eventType: 'product_click',
      productId: product.id,
      productName: product.name,
      location, // where they clicked from: gallery, search, featured, etc.
      timestamp: new Date().toISOString()
    };
    
    logger.logAnalytics('product_click', event);
    this.trackEvent('product_click', event);
  }

  // Cart Tracking
  trackAddToCart(product, quantity = 1) {
    const event = {
      sessionId: this.sessionId,
      eventType: 'add_to_cart',
      productId: product.id,
      productName: product.name,
      productPrice: product.price,
      quantity,
      timestamp: new Date().toISOString()
    };
    
    logger.logAnalytics('add_to_cart', event);
    this.trackEvent('add_to_cart', event);
  }

  trackRemoveFromCart(product, quantity = 1) {
    const event = {
      sessionId: this.sessionId,
      eventType: 'remove_from_cart',
      productId: product.id,
      productName: product.name,
      quantity,
      timestamp: new Date().toISOString()
    };
    
    logger.logAnalytics('remove_from_cart', event);
    this.trackEvent('remove_from_cart', event);
  }

  trackCartView(cartItems, cartTotal) {
    const event = {
      sessionId: this.sessionId,
      eventType: 'cart_view',
      itemCount: cartItems.length,
      cartTotal,
      items: cartItems.map(item => ({
        productId: item.id,
        productName: item.name,
        quantity: item.quantity,
        price: item.price
      })),
      timestamp: new Date().toISOString()
    };
    
    logger.logAnalytics('cart_view', event);
    this.trackEvent('cart_view', event);
  }

  // Checkout Tracking
  trackCheckoutStart(cartItems, cartTotal) {
    const event = {
      sessionId: this.sessionId,
      eventType: 'checkout_start',
      itemCount: cartItems.length,
      cartTotal,
      timestamp: new Date().toISOString()
    };
    
    logger.logAnalytics('checkout_start', event);
    this.trackEvent('checkout_start', event);
  }

  trackCheckoutStep(step, data = {}) {
    const event = {
      sessionId: this.sessionId,
      eventType: 'checkout_step',
      step, // 'shipping', 'payment', 'review'
      ...data,
      timestamp: new Date().toISOString()
    };
    
    logger.logAnalytics('checkout_step', event);
    this.trackEvent('checkout_step', event);
  }

  trackPurchase(orderData) {
    const event = {
      sessionId: this.sessionId,
      eventType: 'purchase',
      orderId: orderData.orderId,
      orderNumber: orderData.orderNumber,
      total: orderData.total,
      itemCount: orderData.items.length,
      items: orderData.items.map(item => ({
        productId: item.productId,
        productName: item.productName,
        quantity: item.quantity,
        price: item.price
      })),
      timestamp: new Date().toISOString()
    };
    
    logger.logAnalytics('purchase', event);
    this.trackEvent('purchase', event);
  }

  // Search Tracking
  trackSearch(searchTerm, resultsCount) {
    const event = {
      sessionId: this.sessionId,
      eventType: 'search',
      searchTerm,
      resultsCount,
      timestamp: new Date().toISOString()
    };
    
    logger.logAnalytics('search', event);
    this.trackEvent('search', event);
  }

  // Filter Tracking
  trackFilter(filterType, filterValue) {
    const event = {
      sessionId: this.sessionId,
      eventType: 'filter',
      filterType, // 'category', 'price', etc.
      filterValue,
      timestamp: new Date().toISOString()
    };
    
    logger.logAnalytics('filter', event);
    this.trackEvent('filter', event);
  }

  // Button Click Tracking
  trackButtonClick(buttonName, location, metadata = {}) {
    const event = {
      sessionId: this.sessionId,
      eventType: 'button_click',
      buttonName,
      location, // which page/component
      ...metadata,
      timestamp: new Date().toISOString()
    };
    
    logger.logAnalytics('button_click', event);
    this.trackEvent('button_click', event);
  }

  // Site-wide delegated click tracking (see utils/siteClickTracking.js) — a label plus the page
  // it happened on, so the dashboard can show "most clicked" without every button/link needing
  // its own trackButtonClick call.
  trackClick(label, page, metadata = {}) {
    this.trackEvent('click', { label, page, ...metadata });
  }

  // Scroll-depth milestones (25/50/75/100%), one per page view — see utils/siteClickTracking.js.
  trackScrollDepth(milestone, page) {
    this.trackEvent('scroll_depth', { milestone, page });
  }

  // Zee Social feed — which YouTube video or Instagram post someone actually opened.
  trackSocialClick(platform, itemId, label) {
    this.trackEvent('social_click', { platform, itemId, label });
  }

  // Custom Event Tracking
  trackEvent(eventType, data = {}) {
    const event = {
      sessionId: this.sessionId,
      eventType,
      ...data,
      timestamp: new Date().toISOString()
    };
    
    this.events.push(event);
    
    // Send to backend
    this.sendToBackend('event', event);
  }

  // Send data to backend
  async sendToBackend(type, data) {
    if (!this.isEnabled) return;

    // Attach stored UTM/click-ID attribution to every event so the backend EventLog carries
    // source/campaign context without every call site having to look it up itself.
    const attribution = getAttribution();
    const payload = {
      ...data,
      businessId: BUSINESS_ID || undefined,
      pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
      landingPageUrl: this.visitorInfo?.landingPage,
      utmSource: attribution?.lastTouch?.source,
      utmMedium: attribution?.lastTouch?.medium,
      utmCampaign: attribution?.lastTouch?.campaign,
      utmContent: attribution?.lastTouch?.content,
      utmTerm: attribution?.lastTouch?.term,
      fbclid: attribution?.fbclid,
      gclid: attribution?.gclid,
      deviceType: getDeviceType(),
    };

    try {
      await fetch(`${this.backendUrl}/api/v1/analytics/${type}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      // Silently fail - don't interrupt user experience
      console.warn('📊 [ANALYTICS] Failed to send analytics:', error.message);
    }
  }

  // Session End
  endSession() {
    const sessionData = {
      sessionId: this.sessionId,
      duration: Math.floor((Date.now() - this.sessionStart) / 1000), // seconds
      pageViews: this.pageViews.length,
      events: this.events.length,
      lastPage: this.currentPage,
      timestamp: new Date().toISOString()
    };
    
    this.trackEvent('session_end', sessionData);
    
    console.log('📊 [ANALYTICS] Session ended', sessionData);
  }

  // Get session summary
  getSessionSummary() {
    return {
      sessionId: this.sessionId,
      duration: Math.floor((Date.now() - this.sessionStart) / 1000),
      pageViews: this.pageViews.length,
      events: this.events.length,
      visitorInfo: this.visitorInfo
    };
  }

  // Get analytics summary (for AnalyticsDashboard compatibility)
  getAnalyticsSummary() {
    // Calculate top pages from pageViews
    const pageCounts = {};
    this.pageViews.forEach(pageView => {
      const page = pageView.path || pageView.pageName || 'Unknown';
      pageCounts[page] = (pageCounts[page] || 0) + 1;
    });

    const topPages = Object.entries(pageCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([page, views]) => ({ page, views }));

    // Extract click events for heatmap
    const clickEvents = this.events.filter(e => e.eventType === 'button_click' || e.eventType === 'product_click');
    const clickHeatmap = clickEvents.map(event => ({
      page: event.page || window.location.pathname,
      x: event.position?.x || 0,
      y: event.position?.y || 0,
      element: event.element?.tagName || 'UNKNOWN',
      timestamp: new Date(event.timestamp).getTime()
    }));

    // Extract scroll events
    const scrollEvents = this.events.filter(e => e.eventType === 'scroll');
    const scrollData = scrollEvents.map(event => ({
      page: event.page || window.location.pathname,
      scrollY: event.scrollY || 0,
      scrollPercentage: event.scrollPercentage || 0,
      timestamp: new Date(event.timestamp).getTime()
    }));

    // Calculate average time on page
    let totalTime = 0;
    let pagesWithTime = 0;
    this.pageViews.forEach(pageView => {
      if (pageView.timeOnPreviousPage > 0) {
        totalTime += pageView.timeOnPreviousPage;
        pagesWithTime++;
      }
    });
    const averageTimeOnPage = pagesWithTime > 0 ? Math.round(totalTime / pagesWithTime) : 0;

    // Recent activity
    const recentActivity = this.events
      .slice(-20)
      .map(event => ({
        type: event.eventType || 'custom',
        timestamp: new Date(event.timestamp).getTime(),
        page: event.page || event.path || window.location.pathname
      }))
      .sort((a, b) => b.timestamp - a.timestamp);

    return {
      totalPageViews: this.pageViews.length,
      totalSessions: 1,
      totalClicks: clickEvents.length,
      totalScrollEvents: scrollEvents.length,
      averageTimeOnPage,
      topPages,
      clickHeatmap: clickHeatmap.slice(-100),
      scrollData: scrollData.slice(-50),
      recentActivity
    };
  }

  // Export analytics data (for AnalyticsDashboard compatibility)
  exportData() {
    return {
      exportDate: new Date().toISOString(),
      sessions: [{
        sessionId: this.sessionId,
        pageViews: this.pageViews,
        events: this.events,
        visitorInfo: this.visitorInfo,
        lastUpdated: new Date().toISOString()
      }],
      currentSession: {
        sessionId: this.sessionId,
        pageViews: this.pageViews,
        events: this.events,
        lastUpdated: new Date().toISOString()
      }
    };
  }

  // Clear analytics data (for AnalyticsDashboard compatibility)
  clearData() {
    this.pageViews = [];
    this.events = [];
    this.sessionId = generateSessionId();
    this.sessionStart = Date.now();
    // Note: analyticsService doesn't persist to localStorage by default
    // but clear it if it exists
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.removeItem('rainbowSails_analytics');
      } catch (error) {
        // Ignore errors
      }
    }
    console.log('📊 [ANALYTICS] Analytics data cleared');
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

// Track session end on page unload
window.addEventListener('beforeunload', () => {
  analyticsService.endSession();
});

// Track visibility changes (tab switching)
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    analyticsService.trackEvent('tab_hidden');
  } else {
    analyticsService.trackEvent('tab_visible');
  }
});

export default analyticsService;
